import { Injectable, OnDestroy, NgZone, inject } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subject, fromEvent, merge } from 'rxjs';
import { environment } from '../../../environments/environment.development';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { CommonService } from '../../shared/services/common.service';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class AppService implements OnDestroy {
  private hubConnection!: signalR.HubConnection;
  private readonly signalRConnectedSubject = new BehaviorSubject<boolean>(
    false
  );
  signalRConnected$ = this.signalRConnectedSubject.asObservable();

  private readonly notificationReceivedSubject = new Subject<{
    subject: string;
    body: string;
  }>();
  notificationReceived$ = this.notificationReceivedSubject.asObservable();

  private readonly connectionStatusSubject =
    new BehaviorSubject<signalR.HubConnectionState>(
      signalR.HubConnectionState.Disconnected
    );
  connectionStatus$ = this.connectionStatusSubject.asObservable();

  private readonly destroy$ = new Subject<void>();
  private readonly networkStatusSubject = new BehaviorSubject<boolean>(
    navigator.onLine
  );
  networkStatus$ = this.networkStatusSubject.asObservable();

  private reconnectAttempt = 0;
  private readonly maxReconnectAttempts = 5;
  private readonly baseReconnectInterval = 2000;
  private connectionInitialized = false;
  private connectionTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionInProgress = false;
  private lastInitializeTime = 0;
  private readonly minInitializeInterval = 5000;
  private static isGlobalAuthErrorHandled = false;
  private static authErrorHandleTime = 0;
  private readonly authErrorCooldown = 5000;
  private currentOrganizationId: number | null = null;

  private readonly commonService = inject(CommonService);
  private readonly router = inject(Router);

  constructor(private readonly ngZone: NgZone) {
    this.monitorNetworkStatus();
  }

  private monitorNetworkStatus(): void {
    this.ngZone.runOutsideAngular(() => {
      merge(fromEvent(window, 'online'), fromEvent(window, 'offline'))
        .pipe(debounceTime(1000), takeUntil(this.destroy$))
        .subscribe(() => {
          this.ngZone.run(() => {
            const isOnline = navigator.onLine;
            this.networkStatusSubject.next(isOnline);

            if (isOnline && this.connectionInitialized) {
              this.restartConnection();
            } else if (!isOnline) {
              this.disconnectSignalR();
            }
          });
        });
    });
  }

  initializeSignalR(): void {
    const now = Date.now();
    if (now - this.lastInitializeTime < this.minInitializeInterval) {
      return;
    }

    this.lastInitializeTime = now;

    if (this.connectionInProgress) {
      return;
    }

    try {
      if (this.connectionTimer) {
        clearTimeout(this.connectionTimer);
        this.connectionTimer = null;
      }

      if (!navigator.onLine) {
        this.connectionStatusSubject.next(
          signalR.HubConnectionState.Disconnected
        );
        return;
      }
      if (this.hubConnection) {
        const currentState = this.hubConnection.state;
        if (currentState === signalR.HubConnectionState.Connected) {
          return;
        }
        if (
          currentState === signalR.HubConnectionState.Connecting ||
          currentState === signalR.HubConnectionState.Reconnecting
        ) {
          return;
        }
        this.connectionInProgress = true;
        this.disconnectSignalR()
          .then(() => {
            setTimeout(() => this.createNewConnection(), 1000);
          })
          .catch(() => {
            this.connectionInProgress = false;
            this.createNewConnection();
          });
      } else {
        this.createNewConnection();
      }
    } catch {
      this.signalRConnectedSubject.next(false);
      this.connectionStatusSubject.next(
        signalR.HubConnectionState.Disconnected
      );
      this.connectionInitialized = false;
      this.connectionInProgress = false;
    }
  }

  private createNewConnection(): void {
    try {
      this.connectionInProgress = true;
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${environment.notificatioHubUrl}`, {
          headers: {
            Accept: 'application/json',
          },
          transport: signalR.HttpTransportType.LongPolling,
          skipNegotiation: false,
          withCredentials: true,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 20000])
        .withHubProtocol(new signalR.JsonHubProtocol())
        .configureLogging(signalR.LogLevel.None)
        .build();

      this.setupConnectionStateHandlers();
      this.setupSignalREventHandlers();
      this.startSignalRConnection();
      this.connectionInitialized = true;
    } catch {
      this.connectionInProgress = false;
      this.signalRConnectedSubject.next(false);
      this.connectionStatusSubject.next(
        signalR.HubConnectionState.Disconnected
      );
    }
  }

  private setupConnectionStateHandlers(): void {
    this.hubConnection.onreconnecting(() => {
      this.connectionStatusSubject.next(
        signalR.HubConnectionState.Reconnecting
      );
      this.signalRConnectedSubject.next(false);
    });

    this.hubConnection.onreconnected(() => {
      this.connectionStatusSubject.next(signalR.HubConnectionState.Connected);
      this.signalRConnectedSubject.next(true);
      this.reconnectAttempt = 0;
      this.connectionInProgress = false;
    });

    this.hubConnection.onclose(error => {
      this.connectionStatusSubject.next(
        signalR.HubConnectionState.Disconnected
      );
      this.signalRConnectedSubject.next(false);
      this.connectionInProgress = false;

      if (error) {
        this.attemptReconnect();
      }
    });
  }

  private setupSignalREventHandlers(): void {
    this.hubConnection.on(
      'ReceiveNotification',
      (subject: string, body: string) => {
        try {
          const notificationData =
            typeof body === 'string' ? body : JSON.parse(body);

          if (
            notificationData.OrgnizationIds &&
            Array.isArray(notificationData.OrgnizationIds)
          ) {
            if (this.currentOrganizationId) {
              if (
                notificationData.OrgnizationIds.includes(
                  this.currentOrganizationId.toString()
                )
              ) {
                this.notificationReceivedSubject.next({ subject, body });
              }
            } else {
              this.notificationReceivedSubject.next({ subject, body });
            }
          } else {
            this.notificationReceivedSubject.next({ subject, body });
          }
        } catch {
          this.notificationReceivedSubject.next({ subject, body });
        }
      }
    );
  }

  private startSignalRConnection(): void {
    if (!this.hubConnection) {
      this.connectionInProgress = false;
      return;
    }

    if (!navigator.onLine) {
      this.connectionStatusSubject.next(
        signalR.HubConnectionState.Disconnected
      );
      this.connectionInProgress = false;
      return;
    }

    if (this.hubConnection.state === signalR.HubConnectionState.Disconnected) {
      this.connectionStatusSubject.next(signalR.HubConnectionState.Connecting);

      const connectionTimeout = setTimeout(() => {
        if (
          this.hubConnection?.state === signalR.HubConnectionState.Connecting
        ) {
          this.hubConnection
            .stop()
            .then(() => {
              this.hubConnection = null as unknown as signalR.HubConnection;
              this.connectionInProgress = false;
            })
            .catch(() => {
              this.hubConnection = null as unknown as signalR.HubConnection;
              this.connectionInProgress = false;
            });
        }
      }, 15000);

      this.hubConnection
        .start()
        .then(() => {
          clearTimeout(connectionTimeout);
          this.signalRConnectedSubject.next(true);
          this.connectionStatusSubject.next(
            signalR.HubConnectionState.Connected
          );
          this.reconnectAttempt = 0;
          this.connectionInProgress = false;
        })
        .catch(() => {
          clearTimeout(connectionTimeout);
          this.signalRConnectedSubject.next(false);
          this.connectionStatusSubject.next(
            signalR.HubConnectionState.Disconnected
          );
          this.connectionInProgress = false;
          this.attemptReconnect();
        });
    } else {
      this.connectionStatusSubject.next(this.hubConnection.state);
      if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
        this.signalRConnectedSubject.next(true);
      }
      this.connectionInProgress = false;
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempt >= this.maxReconnectAttempts) {
      this.connectionInProgress = false;
      return;
    }

    if (!navigator.onLine) {
      this.connectionInProgress = false;
      return;
    }

    this.reconnectAttempt++;
    const delay = Math.min(
      this.baseReconnectInterval * Math.pow(2, this.reconnectAttempt - 1),
      30000
    );

    if (this.connectionTimer) {
      clearTimeout(this.connectionTimer);
    }

    this.connectionTimer = setTimeout(() => {
      this.connectionTimer = null;
      if (
        navigator.onLine &&
        this.hubConnection?.state === signalR.HubConnectionState.Disconnected
      ) {
        this.startSignalRConnection();
      } else {
        this.connectionInProgress = false;
      }
    }, delay);
  }

  getConnectionStatus(): signalR.HubConnectionState {
    return this.hubConnection?.state || signalR.HubConnectionState.Disconnected;
  }

  getHubConnection(): signalR.HubConnection | null {
    return this.hubConnection || null;
  }

  setCurrentOrganization(organizationId: number | null): void {
    this.currentOrganizationId = organizationId;
  }

  getCurrentOrganization(): number | null {
    return this.currentOrganizationId;
  }

  disconnectSignalR(): Promise<void> {
    this.signalRConnectedSubject.next(false);
    this.connectionStatusSubject.next(signalR.HubConnectionState.Disconnected);

    if (this.hubConnection) {
      try {
        this.hubConnection.off('ReceiveNotification');

        if (
          this.hubConnection.state === signalR.HubConnectionState.Connected ||
          this.hubConnection.state === signalR.HubConnectionState.Connecting ||
          this.hubConnection.state === signalR.HubConnectionState.Reconnecting
        ) {
          return this.hubConnection
            .stop()
            .then(() => {
              this.hubConnection = null as unknown as signalR.HubConnection;
              this.connectionInProgress = false;
            })
            .catch(() => {
              this.hubConnection = null as unknown as signalR.HubConnection;
              this.connectionInProgress = false;
            });
        } else {
          this.hubConnection = null as unknown as signalR.HubConnection;
          this.connectionInProgress = false;
        }
      } catch {
        this.hubConnection = null as unknown as signalR.HubConnection;
        this.connectionInProgress = false;
      }
    }

    return Promise.resolve();
  }

  restartConnection(): void {
    if (this.connectionInProgress) {
      return;
    }

    this.connectionInProgress = true;

    this.disconnectSignalR()
      .then(() => {
        setTimeout(() => {
          if (navigator.onLine) {
            this.initializeSignalR();
          } else {
            this.connectionInProgress = false;
          }
        }, 2000);
      })
      .catch(() => {
        this.connectionInProgress = false;
      });
  }

  handleGlobalAuthError(): void {
    const now = Date.now();
    if (
      AppService.isGlobalAuthErrorHandled &&
      now - AppService.authErrorHandleTime < this.authErrorCooldown
    ) {
      return;
    }

    AppService.isGlobalAuthErrorHandled = true;
    AppService.authErrorHandleTime = now;

    this.disconnectSignalR();

    this.commonService.notify(
      'error',
      'Your session has expired. Please login again.'
    );

    this.resetAuthState();

    setTimeout(() => {
      AppService.isGlobalAuthErrorHandled = false;
    }, this.authErrorCooldown);
  }
  private static clearGlobalAuthData(): void {
    const authKeys = [
      'user_data',
      'user_role',
      'verify-user',
      'isOtpVerified',
      'isSupportLogin',
      'login_data',
      'isLMSLoggedIn',
      'returnUrl',
      'authState',
      'permissions',
      'userPreferences',
    ];
    authKeys.forEach(key => {
      try {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
        }
      } catch (error) {
        console.warn(`Failed to remove ${key} from localStorage:`, error);
      }
    });
    try {
      sessionStorage.clear();
    } catch (error) {
      console.warn('Failed to clear sessionStorage:', error);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static isAuthenticationError(error: HttpErrorResponse | any): boolean {
    if (!error) return false;

    if (error.status === 401) return true;
    if (error.statusCode === 401) return true;

    const errorMessage = (
      error.message ||
      error.toString?.() ||
      ''
    ).toLowerCase();
    const authKeywords = [
      'unauthorized',
      'login required',
      '401',
      'authentication',
      'session expired',
    ];

    return (
      authKeywords.some(keyword => errorMessage.includes(keyword)) ||
      (typeof error.error === 'string' &&
        authKeywords.some(keyword =>
          error.error.toLowerCase().includes(keyword)
        ))
    );
  }
  resetAuthState(): void {
    AppService.clearGlobalAuthData();
    setTimeout(() => {
      window.location.replace('/login');
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.connectionTimer) {
      clearTimeout(this.connectionTimer);
      this.connectionTimer = null;
    }

    this.disconnectSignalR();

    this.destroy$.next();
    this.destroy$.complete();

    this.signalRConnectedSubject.complete();
    this.notificationReceivedSubject.complete();
    this.connectionStatusSubject.complete();
    this.networkStatusSubject.complete();
  }
}
